import React from "react";
import { useTranslation } from "react-i18next";
import {
  Upload,
  Warehouse,
  LayoutDashboard,
  Trophy,
  LineChart,
  Zap,
  BookOpen,
  Calendar,
  GitCompareArrows,
  Settings,
  Boxes,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import NavItem from "./NavItem";
import { TooltipProvider } from "@/components/ui/tooltip";

/**
 * Sidebar — all navigation groups (Upload, Reports, Admin, Help).
 *
 * Receives the permission checker and mobile-open flag from Layout so the
 * sidebar stays a pure presentational component.
 *
 * When `collapsed=true` the aside shrinks to an icon-only rail (w-16). The
 * mobile drawer always renders the full width regardless of collapse so users
 * on small screens keep full-labels once they open the drawer.
 */
export default function Sidebar({
  user,
  hasPermission,
  mobileOpen,
  collapsed = false,
  onToggleCollapsed,
}) {
  const { t } = useTranslation();
  const isAdmin = user && user !== false && user.role === "admin";
  const canUpload = hasPermission("upload");

  const showReports =
    hasPermission("report:kpis") ||
    hasPermission("report:ranking") ||
    hasPermission("report:ganador") ||
    hasPermission("report:comparativo") ||
    hasPermission("report:tendencia") ||
    hasPermission("report:distribucion") ||
    hasPermission("report:heatmap") ||
    hasPermission("report:tabla") ||
    hasPermission("report:3d") ||
    hasPermission("report:efficiency");

  const showReportsHub =
    hasPermission("report:ranking") ||
    hasPermission("report:comparativo") ||
    hasPermission("report:tendencia") ||
    hasPermission("report:distribucion") ||
    hasPermission("report:heatmap") ||
    hasPermission("report:tabla") ||
    hasPermission("report:3d");

  // On mobile the aside always uses w-60 (drawer). On desktop it switches
  // between w-60 and w-16 depending on `collapsed`.
  const asideWidth = collapsed ? "lg:w-16" : "lg:w-60";

  return (
    <TooltipProvider>
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-60 transform flex-col border-r border-border bg-card transition-[width,transform] duration-200 lg:translate-x-0",
          asideWidth,
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Brand */}
        <div
          className={cn(
            "flex h-16 items-center border-b border-border",
            collapsed ? "justify-center px-2" : "gap-2 px-5"
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Warehouse className="h-4 w-4" />
          </div>
          {!collapsed && (
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">
                {t("app.name")}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("app.tagline")}
              </div>
            </div>
          )}
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {canUpload && (
            <NavItem
              to="/upload"
              icon={Upload}
              label={t("nav.upload")}
              testId="nav-upload"
              collapsed={collapsed}
            />
          )}

          {/* Reports group */}
          {showReports && (
            <>
              <SectionHeader collapsed={collapsed}>{t("nav.reports")}</SectionHeader>
              {hasPermission("report:kpis") && (
                <NavItem to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} testId="nav-dashboard" collapsed={collapsed} />
              )}
              {hasPermission("report:ganador") && (
                <NavItem to="/reports/ganador" icon={Trophy} label={t("nav.ganador")} testId="nav-ganador" collapsed={collapsed} />
              )}
              {showReportsHub && (
                <NavItem
                  to="/reports"
                  icon={LineChart}
                  label={t("nav.reportsHub")}
                  testId="nav-reports-hub"
                  collapsed={collapsed}
                />
              )}
              {hasPermission("report:comparativo") && (
                <NavItem
                  to="/comparatives"
                  icon={GitCompareArrows}
                  label={t("nav.comparatives", { defaultValue: "Comparativos" })}
                  testId="nav-comparatives"
                  collapsed={collapsed}
                />
              )}
              {hasPermission("report:efficiency") && (
                <NavItem to="/reports/efficiency" icon={Zap} label={t("nav.efficiency")} testId="nav-efficiency" collapsed={collapsed} />
              )}
            </>
          )}

          {hasPermission("schedule:view") && (
            <NavItem
              to="/schedule"
              icon={Calendar}
              label={t("nav.schedule")}
              testId="nav-schedule"
              collapsed={collapsed}
            />
          )}

          {hasPermission("catalog:view") && (
            <NavItem
              to="/catalog"
              icon={Warehouse}
              label={t("nav.catalog")}
              testId="nav-catalog"
              collapsed={collapsed}
            />
          )}

          {hasPermission("catalog:view") && (
            <NavItem
              to="/inventory"
              icon={Boxes}
              label={t("nav.inventory", { defaultValue: "Inventario" })}
              testId="nav-inventory"
              collapsed={collapsed}
            />
          )}

          {isAdmin && (
            <>
              <SectionHeader collapsed={collapsed}>{t("nav.admin")}</SectionHeader>
              <NavItem
                to="/admin"
                icon={Settings}
                label={t("nav.admin")}
                testId="nav-admin"
                collapsed={collapsed}
              />
            </>
          )}

          <SectionHeader collapsed={collapsed}>{t("nav.helpGroup")}</SectionHeader>
          <NavItem to="/help" icon={BookOpen} label={t("nav.help")} testId="nav-help" collapsed={collapsed} />
        </nav>

        {/* Footer toggle — hidden on mobile (drawer already covers it) */}
        {onToggleCollapsed && (
          <div className="hidden border-t border-border p-2 lg:block">
            <button
              type="button"
              onClick={onToggleCollapsed}
              data-testid="sidebar-collapse-toggle"
              aria-label={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
              title={collapsed ? "Expandir" : "Colapsar"}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground",
                collapsed && "justify-center"
              )}
            >
              {collapsed ? (
                <ChevronsRight className="h-4 w-4" />
              ) : (
                <>
                  <ChevronsLeft className="h-4 w-4" />
                  <span>Colapsar</span>
                </>
              )}
            </button>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}

function SectionHeader({ collapsed, children }) {
  if (collapsed) {
    // Discrete divider replaces the label to keep visual grouping without text.
    return <div className="my-2 border-t border-border/50" aria-hidden="true" />;
  }
  return (
    <div className="mt-4 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}
